import type {
  TrialAdvertisingStatus,
  TrialApplication,
  TrialAttributionCaptureInfo,
  TrialAttributionSnapshot,
  TrialTrafficSource,
} from "@/types/trial";

const STORAGE_KEY = "sumacomi:trial-attribution:v1";
const INSTAGRAM_CONTENT_MARKERS = [
  "story",
  "stories",
  "post",
  "feed",
  "reel",
  "reels",
];

export const TRIAL_ATTRIBUTION_FILTER_OPTIONS = [
  { value: "MEMBER_APP", label: "会員アプリ" },
  { value: "MEMBER_STAFF", label: "管理画面の代理受付" },
  { value: "META_AD", label: "Meta広告" },
  { value: "INSTAGRAM_PROFILE", label: "Instagramプロフィール経由" },
  { value: "INSTAGRAM_CONTENT", label: "Instagram投稿・ストーリーズ経由" },
  { value: "META_ORGANIC", label: "Metaオーガニック流入" },
  { value: "META_UNKNOWN", label: "Meta流入（広告判定不可）" },
  { value: "GOOGLE_AD", label: "Google広告" },
  { value: "GOOGLE_ORGANIC", label: "Google自然検索" },
  { value: "GOOGLE_UNKNOWN", label: "Google流入（広告判定不可）" },
  { value: "DIRECT", label: "直接流入" },
  { value: "REFERRAL", label: "他サイトからの流入" },
  { value: "OTHER", label: "その他" },
  { value: "INSTAGRAM", label: "Instagram" },
  { value: "INSTAGRAM_DM", label: "Instagram DM" },
  { value: "LINE", label: "LINE" },
  { value: "X", label: "X（旧Twitter）" },
  { value: "FLYER", label: "チラシ" },
  { value: "PHONE", label: "電話" },
  { value: "WEB_SEARCH", label: "Web検索" },
  { value: "WEB", label: "Web" },
  { value: "PERSONAL_REFERRAL", label: "紹介" },
] as const;

export interface TrialAttributionView {
  sourceKey: string;
  /** 一覧・絞り込み向けに広告判定を含めた分類名。 */
  sourceLabel: string;
  /** 詳細画面向けの媒体名。広告判定とは混ぜない。 */
  trafficSourceLabel: string;
  advertisingStatus: TrialAdvertisingStatus | null;
  advertisingStatusLabel: string;
  raw: TrialAttributionSnapshot;
  landingPageLabel: string;
  campaignName?: string;
  adName?: string;
  sourceDetail?: string;
  capture?: TrialAttributionCaptureInfo;
}

function normalized(value?: string): string {
  return value?.trim().toLowerCase() ?? "";
}

function attributionPresentation(
  source: TrialTrafficSource,
  status: TrialAdvertisingStatus | null
): { key: string; label: string } {
  switch (source) {
    case "META":
      if (status === "PAID") return { key: "META_AD", label: "Meta広告" };
      if (status === "ORGANIC")
        return { key: "META_ORGANIC", label: "Metaオーガニック流入" };
      return { key: "META_UNKNOWN", label: "Meta流入（広告判定不可）" };
    case "GOOGLE":
      if (status === "PAID") return { key: "GOOGLE_AD", label: "Google広告" };
      if (status === "ORGANIC")
        return { key: "GOOGLE_ORGANIC", label: "Google自然検索" };
      return { key: "GOOGLE_UNKNOWN", label: "Google流入（広告判定不可）" };
    case "INSTAGRAM_PROFILE":
      return { key: source, label: "Instagramプロフィール経由" };
    case "INSTAGRAM_CONTENT":
      return { key: source, label: "Instagram投稿・ストーリーズ経由" };
    case "INSTAGRAM":
      return { key: source, label: "Instagram" };
    case "INSTAGRAM_DM":
      return { key: source, label: "Instagram DM" };
    case "LINE":
      return { key: source, label: "LINE" };
    case "X":
      return { key: source, label: "X（旧Twitter）" };
    case "FLYER":
      return { key: source, label: "チラシ" };
    case "PHONE":
      return { key: source, label: "電話" };
    case "WEB_SEARCH":
      return { key: source, label: "Web検索" };
    case "WEB":
      return { key: source, label: "Web" };
    case "DIRECT":
      return { key: source, label: "直接流入" };
    case "REFERRAL":
      return { key: source, label: "他サイトからの流入" };
    case "PERSONAL_REFERRAL":
      return { key: source, label: "紹介" };
    case "OTHER":
      return { key: source, label: "その他" };
  }
}

function advertisingStatusLabel(status: TrialAdvertisingStatus | null): string {
  if (!status) return "記録なし";
  switch (status) {
    case "PAID":
      return "広告";
    case "ORGANIC":
      return "非広告（オーガニック）";
    case "UNKNOWN":
      return "判定不可";
    case "NOT_APPLICABLE":
      return "対象外";
  }
}

function trafficSourceOnlyLabel(source: TrialTrafficSource): string {
  switch (source) {
    case "META":
      return "Meta";
    case "GOOGLE":
      return "Google";
    case "INSTAGRAM_PROFILE":
      return "Instagramプロフィール経由";
    case "INSTAGRAM_CONTENT":
      return "Instagram投稿・ストーリーズ経由";
    case "INSTAGRAM":
      return "Instagram";
    case "INSTAGRAM_DM":
      return "Instagram DM";
    case "LINE":
      return "LINE";
    case "X":
      return "X（旧Twitter）";
    case "FLYER":
      return "チラシ";
    case "PHONE":
      return "電話";
    case "WEB_SEARCH":
      return "Web検索";
    case "WEB":
      return "Web";
    case "DIRECT":
      return "直接流入";
    case "REFERRAL":
      return "他サイトからの流入";
    case "PERSONAL_REFERRAL":
      return "紹介";
    case "OTHER":
      return "その他";
  }
}

function isGenericContentName(value: string): boolean {
  const normalizedValue = normalized(value);
  return (
    normalizedValue === "link_in_bio" ||
    INSTAGRAM_CONTENT_MARKERS.some((marker) => normalizedValue === marker)
  );
}

/** 正規化済みの申込データを、管理画面表示へ変換する。 */
export function resolveTrialAttribution(
  application: Pick<
    TrialApplication,
    | "trafficSource"
    | "trafficSourceDetail"
    | "advertisingStatus"
    | "attributionData"
    | "origin"
    | "memberRequestChannel"
  >
): TrialAttributionView {
  const raw = sanitizeSnapshot(application.attributionData);
  if (application.origin === "MEMBER") {
    const isStaffReception = application.memberRequestChannel === "STAFF";
    return {
      sourceKey: isStaffReception ? "MEMBER_STAFF" : "MEMBER_APP",
      sourceLabel: isStaffReception ? "管理画面の代理受付" : "会員アプリ",
      trafficSourceLabel: isStaffReception ? "スタッフ代理受付" : "会員アプリ",
      advertisingStatus: null,
      advertisingStatusLabel: "対象外",
      raw,
      landingPageLabel: isStaffReception ? "管理画面" : "会員アプリ",
    };
  }
  const trafficSource = application.trafficSource;
  const status = application.advertisingStatus;
  const presentation = trafficSource
    ? attributionPresentation(trafficSource, status)
    : { key: "", label: "記録なし" };

  const paid = status === "PAID";
  return {
    sourceKey: presentation.key,
    sourceLabel: presentation.label,
    trafficSourceLabel: trafficSource
      ? trafficSourceOnlyLabel(trafficSource)
      : presentation.label,
    advertisingStatus: status,
    advertisingStatusLabel: advertisingStatusLabel(status),
    raw,
    landingPageLabel:
      raw.landingPage === "/" ? "トップページ" : raw.landingPage || "記録なし",
    campaignName: paid ? raw.utmCampaign : undefined,
    adName:
      paid && raw.utmContent && !isGenericContentName(raw.utmContent)
        ? raw.utmContent
        : undefined,
    sourceDetail: application.trafficSourceDetail?.trim() || undefined,
    capture: sanitizeCaptureInfo(application.attributionData?.capture),
  };
}

function capped(value: string | null, length: number): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, length) : undefined;
}

function sanitizeSnapshot(input: unknown): TrialAttributionSnapshot {
  if (!input || typeof input !== "object" || Array.isArray(input)) return {};
  const source = input as Record<string, unknown>;
  const result: TrialAttributionSnapshot = {};
  const limits: Record<keyof TrialAttributionSnapshot, number> = {
    utmSource: 255,
    utmMedium: 255,
    utmCampaign: 255,
    utmContent: 255,
    utmTerm: 255,
    landingPage: 2048,
    referrer: 2048,
    fbclid: 512,
    gclid: 512,
  };
  for (const key of Object.keys(limits) as (keyof TrialAttributionSnapshot)[]) {
    const value = source[key];
    if (typeof value === "string") {
      const safeValue = capped(value, limits[key]);
      if (safeValue) {
        const sanitized =
          key === "landingPage"
            ? measurementUrl(safeValue, true)
            : key === "referrer"
              ? measurementUrl(safeValue, false)
              : safeValue;
        if (sanitized) result[key] = sanitized;
      }
    }
  }
  return result;
}

/** 任意のクエリ・フラグメント・認証情報は計測URLに保存しない。 */
function measurementUrl(value: string, landing: boolean): string | undefined {
  try {
    const relative = value.startsWith("/") && !value.startsWith("//");
    const url = new URL(value, "https://attribution.invalid");
    if (!["http:", "https:"].includes(url.protocol)) return undefined;
    if (!relative && !/^https?:\/\//i.test(value)) return undefined;
    const params = new URLSearchParams();
    if (landing) {
      for (const key of ["templateId", "embed"]) {
        const v = url.searchParams.get(key)?.trim();
        if (v) params.set(key, v.slice(0, 255));
      }
    }
    const query = params.toString();
    return capped(
      `${relative ? "" : url.origin}${url.pathname}${query ? `?${query}` : ""}`,
      2048
    );
  } catch {
    return undefined;
  }
}

function externalReferrer(
  referrer: string,
  origin: string
): string | undefined {
  if (!referrer) return undefined;
  try {
    return new URL(referrer).origin === origin
      ? undefined
      : measurementUrl(referrer, false);
  } catch {
    return undefined;
  }
}

export function attributionFromLocation(
  href: string,
  referrer: string,
  origin: string
): TrialAttributionSnapshot {
  const url = new URL(href, origin === "null" ? undefined : origin);
  const params = url.searchParams;
  const explicitLandingPage =
    params.get("landing") ?? params.get("landing_page");
  const pageParameters = new URLSearchParams();
  // 申込フォームを特定するために必要な値だけを残す。
  // UTMやクリックIDは別項目で保持し、任意のクエリ文字列は保存しない。
  for (const key of ["templateId", "embed"] as const) {
    const value = params.get(key)?.trim();
    if (value) pageParameters.set(key, value);
  }
  const pageParameterString = pageParameters.toString();
  const currentPage = `${url.pathname || "/"}${
    pageParameterString ? `?${pageParameterString}` : ""
  }`;
  return {
    utmSource: capped(params.get("utm_source") ?? params.get("source"), 255),
    utmMedium: capped(params.get("utm_medium") ?? params.get("medium"), 255),
    utmCampaign: capped(
      params.get("utm_campaign") ?? params.get("campaign"),
      255
    ),
    utmContent: capped(params.get("utm_content") ?? params.get("content"), 255),
    utmTerm: capped(params.get("utm_term") ?? params.get("term"), 255),
    landingPage:
      measurementUrl(explicitLandingPage ?? currentPage, true) ?? currentPage,
    referrer: externalReferrer(referrer, origin),
    fbclid: capped(params.get("fbclid"), 512),
    gclid: capped(params.get("gclid"), 512),
  };
}

function currentUrlHasTrackingParameters(
  href: string,
  origin: string
): boolean {
  const params = new URL(href, origin === "null" ? undefined : origin)
    .searchParams;
  return [
    "utm_source",
    "source",
    "utm_medium",
    "medium",
    "utm_campaign",
    "campaign",
    "utm_content",
    "content",
    "utm_term",
    "term",
    "landing",
    "landing_page",
    "fbclid",
    "gclid",
  ].some((key) => Boolean(params.get(key)?.trim()));
}

type StorageRead = {
  snapshot?: TrialAttributionSnapshot;
  capture?: TrialAttributionCaptureInfo;
  storageStatus: TrialAttributionCaptureInfo["storageStatus"];
  reason?: TrialAttributionCaptureInfo["reason"];
};
let memoryEntry:
  | {
      scope: string;
      snapshot: TrialAttributionSnapshot;
      capture: TrialAttributionCaptureInfo;
    }
  | undefined;
let captureFailed = false;

function pageScope(href: string): string {
  const url = new URL(
    href,
    window.location.origin === "null" ? undefined : window.location.origin
  );
  const facility = url.pathname.match(
    /^\/(?:trial|embed\/calendar)\/([^/]+)/
  )?.[1];
  return `${url.origin}/${facility ?? "trial"}`;
}

function browserContext(): TrialAttributionCaptureInfo["context"] {
  if (typeof window === "undefined") return "UNKNOWN";
  try {
    return window.self === window.top ? "TOP_LEVEL" : "IFRAME";
  } catch {
    return "UNKNOWN";
  }
}

function readStored(scope: string): StorageRead {
  try {
    const raw = window.sessionStorage.getItem(`${STORAGE_KEY}:${scope}`);
    // 旧キーは施設を確認できるスナップショットだけ引き継ぐ。
    const legacy = raw ? null : window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw && !legacy) return { storageStatus: "AVAILABLE" };
    const input: unknown = JSON.parse((raw ?? legacy)!);
    const parsed = sanitizeSnapshot(input);
    if (
      !raw &&
      (!parsed.landingPage ||
        pageScope(new URL(parsed.landingPage, window.location.href).href) !==
          scope)
    ) {
      return { storageStatus: "AVAILABLE" };
    }
    if (Object.keys(parsed).length === 0)
      return { storageStatus: "AVAILABLE", reason: "STORED_DATA_INVALID" };
    const capture =
      input && typeof input === "object" && "capture" in input
        ? sanitizeCaptureInfo(input.capture)
        : undefined;
    return { snapshot: parsed, capture, storageStatus: "AVAILABLE" };
  } catch (error) {
    return error instanceof SyntaxError
      ? { storageStatus: "AVAILABLE", reason: "STORED_DATA_INVALID" }
      : { storageStatus: "UNAVAILABLE" };
  }
}

export function readStoredTrialAttribution():
  TrialAttributionSnapshot | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return readStored(pageScope(window.location.href)).snapshot;
  } catch {
    return undefined;
  }
}

function sanitizeCaptureInfo(
  input: unknown
): TrialAttributionCaptureInfo | undefined {
  if (!input || typeof input !== "object") return undefined;
  const value = input as TrialAttributionCaptureInfo;
  if (
    ![
      "PAGE_LOAD",
      "SESSION_STORAGE",
      "SUBMIT_FALLBACK",
      "UNAVAILABLE",
    ].includes(value.method) ||
    !["AVAILABLE", "UNAVAILABLE", "NOT_ATTEMPTED"].includes(
      value.storageStatus
    ) ||
    !["TOP_LEVEL", "IFRAME", "UNKNOWN"].includes(value.context)
  )
    return undefined;
  const reason = [
    "ENTRY_NOT_CAPTURED",
    "CAPTURE_FAILED",
    "STORED_DATA_INVALID",
    "BROWSER_UNAVAILABLE",
  ].includes(value.reason ?? "")
    ? value.reason
    : undefined;
  return {
    method: value.method,
    storageStatus: value.storageStatus,
    context: value.context,
    ...(reason ? { reason } : {}),
  };
}

export function captureTrialAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    const scope = pageScope(window.location.href);
    const stored = readStored(scope);
    const previous = memoryEntry?.scope === scope ? memoryEntry : undefined;
    const existing = previous?.snapshot ?? stored.snapshot;
    const current = sanitizeSnapshot(
      attributionFromLocation(
        window.location.href,
        document.referrer,
        window.location.origin
      )
    );
    const newTracking = currentUrlHasTrackingParameters(
      window.location.href,
      window.location.origin
    );
    // 同じUTMの内部遷移や再読込では、開始ページと外部参照元を上書きしない。
    const trackingKeys = [
      "utmSource",
      "utmMedium",
      "utmCampaign",
      "utmContent",
      "utmTerm",
      "fbclid",
      "gclid",
    ] as const;
    const params = new URL(window.location.href).searchParams;
    const changed =
      trackingKeys.some(
        (key) => current[key] !== undefined && current[key] !== existing?.[key]
      ) ||
      ((params.has("landing") || params.has("landing_page")) &&
        current.landingPage !== existing?.landingPage);
    const reuse = existing && (!newTracking || !changed);
    const reason =
      stored.reason ??
      (reuse
        ? (previous?.capture.reason ?? stored.capture?.reason)
        : undefined);
    memoryEntry = {
      scope,
      snapshot: reuse ? existing : current,
      capture: {
        method: reuse
          ? (previous?.capture.method ?? "SESSION_STORAGE")
          : "PAGE_LOAD",
        storageStatus:
          previous?.capture.storageStatus === "UNAVAILABLE" ||
          (reuse && stored.capture?.storageStatus === "UNAVAILABLE")
            ? "UNAVAILABLE"
            : stored.storageStatus,
        context: reuse
          ? (previous?.capture.context ??
            stored.capture?.context ??
            browserContext())
          : browserContext(),
        ...(reason ? { reason } : {}),
      },
    };
    captureFailed = false;
    // メモリに保持してから保存する。保存失敗でも申込の情報は失わない。
    try {
      window.sessionStorage.setItem(
        `${STORAGE_KEY}:${scope}`,
        JSON.stringify({
          ...memoryEntry.snapshot,
          capture: memoryEntry.capture,
        })
      );
    } catch {
      memoryEntry.capture.storageStatus = "UNAVAILABLE";
    }
  } catch {
    captureFailed = true;
  }
}

/** 送信時の共通入口。補完したページと初回流入は区別して送信する。 */
export function getTrialAttributionForSubmission(): {
  attribution?: TrialAttributionSnapshot;
  attributionCapture: TrialAttributionCaptureInfo;
} {
  const context = browserContext();
  if (typeof window === "undefined") {
    return {
      attributionCapture: {
        method: "UNAVAILABLE",
        storageStatus: "NOT_ATTEMPTED",
        context,
        reason: "BROWSER_UNAVAILABLE",
      },
    };
  }
  let storageStatus: TrialAttributionCaptureInfo["storageStatus"] =
    "NOT_ATTEMPTED";
  try {
    const scope = pageScope(window.location.href);
    const stored = readStored(scope);
    storageStatus = stored.storageStatus;
    const entry = memoryEntry?.scope === scope ? memoryEntry : undefined;
    if (entry) {
      return {
        attribution: { ...entry.snapshot },
        attributionCapture: {
          ...entry.capture,
          storageStatus:
            entry.capture.storageStatus === "UNAVAILABLE"
              ? "UNAVAILABLE"
              : storageStatus,
        },
      };
    }
    if (stored.snapshot) {
      return {
        attribution: stored.snapshot,
        attributionCapture: {
          method: "SESSION_STORAGE",
          storageStatus:
            stored.capture?.storageStatus === "UNAVAILABLE"
              ? "UNAVAILABLE"
              : storageStatus,
          context: stored.capture?.context ?? context,
          ...(stored.capture?.reason ? { reason: stored.capture.reason } : {}),
        },
      };
    }
    const attribution = sanitizeSnapshot(
      attributionFromLocation(
        window.location.href,
        document.referrer,
        window.location.origin
      )
    );
    return {
      attribution,
      attributionCapture: {
        method: "SUBMIT_FALLBACK",
        storageStatus,
        context,
        reason:
          stored.reason ??
          (captureFailed ? "CAPTURE_FAILED" : "ENTRY_NOT_CAPTURED"),
      },
    };
  } catch {
    return {
      attributionCapture: {
        method: "UNAVAILABLE",
        storageStatus,
        context,
        reason: "CAPTURE_FAILED",
      },
    };
  }
}
